// hash.ts — FNV-1a 32-bit state fingerprint over the SoA arrays.
//
// Used by the determinism test and debug output to prove that two independent
// runs of the same config reach byte-identical state. Pure integer math, no
// allocation, fixed iteration order.

const FNV_OFFSET_BASIS = 0x811c9dc5;
const FNV_PRIME = 0x01000193;

/** Start a fresh FNV-1a accumulator. */
export function fnvInit(): number {
  return FNV_OFFSET_BASIS;
}

/** Mix one 8-bit byte into the running hash. */
export function fnvByte(hash: number, byte: number): number {
  // XOR low byte, then multiply by the FNV prime (imul keeps it 32-bit).
  return Math.imul(hash ^ (byte & 0xff), FNV_PRIME) >>> 0;
}

/**
 * Mix a full 32-bit word in, low byte first (stable, endianness-independent
 * because we pick the order explicitly).
 */
export function fnvU32(hash: number, value: number): number {
  let h = hash;
  h = fnvByte(h, value & 0xff);
  h = fnvByte(h, (value >>> 8) & 0xff);
  h = fnvByte(h, (value >>> 16) & 0xff);
  h = fnvByte(h, (value >>> 24) & 0xff);
  return h >>> 0;
}

/**
 * Mix a contiguous prefix [0, count) of an Int32Array / Uint32Array into the
 * hash. Iteration order is the array index order — deterministic.
 */
export function fnvI32Array(
  hash: number,
  arr: Int32Array | Uint32Array,
  count: number,
): number {
  let h = hash;
  for (let i = 0; i < count; i++) {
    // arr[i] is typed `number` (typed arrays, no noUncheckedIndexedAccess gap).
    h = fnvU32(h, arr[i] as number);
  }
  return h >>> 0;
}

/** Format a 32-bit hash as a fixed-width hex string for stable logging. */
export function hashHex(hash: number): string {
  return (hash >>> 0).toString(16).padStart(8, '0');
}
